/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import styles from '../styles.module.scss';
import Hidden from '@/components/Hidden';
import SearchPageTemplate from '../SearchPageTemplate/SearchPageTemplate';
import { useSearchPartTypes } from '../../api/getSearchPartTypes';
import { SearchNoResults } from '../SearchComponent/SearchNoResults';
import { countryCodes } from '@/constants/locale';
import { useLocale } from '@/hooks/useLocale';
import { useDealDrawer } from '@/features/deals/hooks/useDealDrawer';
import { useRouter } from 'next/router';
import { usePreferredVehicle } from '@/features/header/hooks/usePreferredVehicle';
import { type HandleFilterType } from '@/types/filterTypes';
import { View } from '@az/starc-ui';

type Props = {
  viewingPartTypes: boolean;
  handleFilter?: (filter: HandleFilterType) => void;
};

const BottomSection = ({ viewingPartTypes, handleFilter }: Props) => {
  const router = useRouter();
  const locale = useLocale();
  const preferredVehicle = usePreferredVehicle();
  const { handleDealClick } = useDealDrawer();
  const { data: searchPartTypesData } = useSearchPartTypes({
    enabled: viewingPartTypes,
    viewingPartTypes,
  });

  const vehicleName = preferredVehicle?.vehicleName;
  const rppOptionsData = '12,24,36,48';
  const queryDisplayLabel =
    typeof router.query.searchText === 'string' ? router.query.searchText : '';
  const leftColumn = {
    [countryCodes.us]:
      '<style> .partsHeader { font-family: "AutoZoneCond", Arial, Helvetica, sans-serif; font-size: 20px; text-transform: uppercase; color: #202124; line-height: 1.25; margin-left: -20px; width: 230px; height: 25px; letter-spacing: 0.75px; } .partsItems { font-family: "HelveticaNeue", Arial, sans-serif; font-size: 14px; color: #484848; line-height: 0.5; padding: 10px 0; margin-left: -20px; text-decoration: none; } a.parts { text-decoration: none; color: #484848; } @media only screen and (max-width: 1140px) { .partsContainer { padding-bottom: 20px; }}</style> <div class="partsContainer"><div class="partsHeader">Popular Parts</div><div class="partsItems"><a class="parts" href="/external-engine/air-filter">Air Filter</a></div> <div class="partsItems"><a class="parts" href="/antifreeze-radiator-additives-and-windshield-wash-fluid/antifreeze">Antifreeze</a></div> <div class="partsItems"><a class="parts" href="/batteries-starting-and-charging/battery">Batteries</a></div> <div class="partsItems"><a class="parts" href="/brakes-and-traction-control/brake-pads">Brakes</a></div> <div class="partsItems"><a class="parts" href="/parts/electrical-and-lighting/headlight">Headlights</a></div> <div class="partsItems"><a class="parts" href="/motor-oil-and-transmission-fluid/engine-oil">Motor Oil</a></div> <div class="partsItems"><a class="parts" href="/ignition-tune-up-and-routine-maintenance/spark-plug">Spark Plugs</a></div> <div class="partsItems"><a class="parts" href="/ignition-tune-up-and-routine-maintenance/wiper-blade-windshield">Wiper Blades</a></div></div>',
    [countryCodes.mx]:
      '<div style="max-width: 1600px; margin: 0px auto; width: auto;"><div data-testid="dynamic-content"><style>   .partsHeader {     font-family: "AutoZoneCondensed", Arial, sans-serif;     font-size: 20px;     text-transform: uppercase;     color: #202124;     line-height: 1.25;     margin-left: -20px;     width: 230px;     height: 60px;     letter-spacing: 0.75px;   }   .partsItems {     font-family: "AutoZone", "HelveticaNeue", Arial, sans-serif;     font-size: 14px;     color: #484848;     line-height: 0.5;     padding: 10px 0;     margin-left: -20px;     text-decoration: none;   }   a.parts {     text-decoration: none;     color: #484848;   }   @media only screen and (max-width: 1140px) {     .partsContainer {       padding-bottom: 20px;     }   } </style> <div class="partsContainer">   <div class="partsHeader">Artículos más buscados</div>   <div class="partsItems">     <a class="parts" href="/aceite-para-motor-y-aditivos-para-aceite/aceite-de-motor">Aceites</a>   </div>   <div class="partsItems">     <a class="parts" href="/anticongelante-aditivos-para-radiador-y-liquidos-limpiaparabrisas/anticongelante-refrigerante">Anticongelantes</a>   </div>   <div class="partsItems">     <a class="parts" href="/frenos-y-control-de-traccion/balata">Balatas</a>   </div>   <div class="partsItems">     <a class="parts" href="/encendido-afinacion-y-mantenimiento-de-rutina/bateria">Baterías</a>   </div>   <div class="partsItems">     <a class="parts" href="/encendido-afinacion-y-mantenimiento-de-rutina/bujia">Bujías</a>   </div>   <div class="partsItems">     <a class="parts" href="/partes-de-colision-carroceria-y-herrajes/faro-bulbo-delantero">Faros</a>   </div>   <div class="partsItems">     <a class="parts" href="/filtros/filtro-de-aire">Filtros de aire</a>   </div>   <div class="partsItems">     <a class="parts" href="/encendido-afinacion-y-mantenimiento-de-rutina/limpiaparabrisas-hoja">Limpiaparabrisas</a>   </div> </div></div></div>',
    [countryCodes.ptBr]:
      '<div style="max-width:1600px;margin:0 auto;width:auto"><div data-testid=dynamic-content><style>.partsHeader{font-family:AutoZoneCondensed,Arial,sans-serif;font-size:20px;text-transform:uppercase;color:#202124;line-height:1.25;margin-left:-20px;width:230px;height:60px;letter-spacing:.75px}.partsItems{font-family:AutoZone,HelveticaNeue,Arial,sans-serif;font-size:14px;color:#484848;line-height:.5;padding:10px 0;margin-left:-20px;text-decoration:none}a.parts{text-decoration:none;color:#484848}@media only screen and (max-width:1140px){.partsContainer{padding-bottom:20px}}</style><div class=partsContainer><div class=partsHeader>Produtos mais pesquisados</div><div class=partsItems><a class=parts href=/oleo-do-motor-e-fluido-da-transmissao/oleo-de-motor>Óleo de Motor</a></div><div class=partsItems><a class=parts href=/controle-de-frenagem-e-tracao/disco-de-freio-dianteiro>Discos de Freio</a></div><div class=partsItems><a class=parts href=/controle-de-frenagem-e-tracao/pastilha-de-freio-dianteira>Pastilhas de Freio</a></div><div class=partsItems><a class=parts href=/bateria-carregadores-e-auxiliares-de-partida/bateria>Baterías</a></div><div class=partsItems><a class=parts href=/ignicao-ajuste-e-manutencao-de-rotina/vela-de-ignicao>Velas Automotivas</a></div><div class=partsItems><a class=parts href=/eletrica-e-iluminacao/lampada-farol-de-milha>Lâmpadas</a></div><div class=partsItems><a class=parts href=/controles-de-temperatura-e-aquecimento/radiador>Radiadores</a></div><div class=partsItems><a class=parts href=/suspensao-direcao-pneu-e-roda/amortecedor-suspensao-dianteira>Amortecedor</a></div></div></div></div>',
  };

  return (
    <View className={styles.searchContentContainer}>
      <View className={styles.searchLeftContainer}>
        {searchPartTypesData?.records?.length ? (
          <Hidden mdDown={!viewingPartTypes}>
            <SearchPageTemplate
              viewingPartTypes={viewingPartTypes}
              onFilterSelect={handleFilter}
              content={[{ '@type': 'SearchLeftNav_FilterNav' }]}
            />
          </Hidden>
        ) : (
          <SearchPageTemplate
            onFilterSelect={handleFilter}
            viewingPartTypes={viewingPartTypes}
            content={[
              {
                tablet: true,
                desktop: true,
                sanitize_this: 'content',
                '@type': 'SearchLeftNav_HTMLBlock',
                name: 'Popular Parts HTML',
                mobile: true,
                sanitizationCheck: true,
                // TODO: When this is migrated to ContentStack it should be its own entry
                content: leftColumn[locale],
              },
            ]}
          />
        )}
      </View>
      <View className={styles.searchRightContainer}>
        {searchPartTypesData?.records?.length ? (
          <SearchPageTemplate
            content={[
              {
                '@type': 'SearchPageBody_YMME',
                desktop: true,
                layoutCode: 'Thin',
                mobile: true,
                name: 'YMME Content Block',
                tablet: true,
              },
              { '@type': 'SearchPageBody_AZCombinedSearchResults' },
            ]}
            queryDisplayLabel={queryDisplayLabel}
            viewingPartTypes={viewingPartTypes}
            onFilterSelect={handleFilter}
            handleDealClick={handleDealClick}
            rppOptionsData={rppOptionsData}
          />
        ) : (
          <>
            <SearchPageTemplate
              viewingPartTypes={viewingPartTypes}
              content={[
                {
                  '@type': 'SearchPageBody_YMME',
                  desktop: true,
                  layoutCode: 'Thin',
                  mobile: true,
                  name: 'YMME Content Block',
                  tablet: true,
                },
              ]}
              queryDisplayLabel={queryDisplayLabel}
              rppOptionsData={rppOptionsData}
            />
            <SearchNoResults vehicleName={vehicleName} queryDisplayLabel={queryDisplayLabel} />
          </>
        )}
      </View>
    </View>
  );
};

export default BottomSection;
